import { useState, useEffect } from "react";
import useFetchWithMsal from "../../../hooks/useFetchWithMsal";
import { protectedResources } from "../../../authConfig";
import { useUser } from "../../../contexts/UserContext";

const NOT_FOUND_INDEX = -1;

const useApproveLines = (tabValue) => {
  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });
  const { user } = useUser();
  
  const [ordersList, setOrdersList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [serialNumbers, setSerialNumbers] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchOrders = async () => {
    setLoading(true);
    try {
      setOrdersList([]);
      
      const ordersResponse = await execute(
        "GET",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/approveLines/getOrders`
      );
      
      const ordersWithIds = ordersResponse.ordersToApprove.map((order, index) => ({
        ...order,
        _uniqueId: `${order.ordname}_${index}`,
      }));
      
      setOrdersList(ordersWithIds);
    } catch (error) {
      showError("Failed to load orders. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [tabValue]);

  const handleQuantityChange = (orderIndex, lineIndex, event) => {
    const newQuant = event.target.value !== "" ? parseInt(event.target.value) : 0;
    const originalQuant = ordersList[orderIndex].lines[lineIndex].quant;

    if (newQuant <= originalQuant) {
      updateOrderLine(orderIndex, lineIndex, { newQuant });
    } else {
      showError(`New quantity cannot exceed original quantity: ${originalQuant}`);
    }
  };

  const handleLineCheckboxChange = (orderIndex, lineIndex) => {
    const line = ordersList[orderIndex].lines[lineIndex];
    const checked = line.checked === undefined ? true : !line.checked;
    updateOrderLine(orderIndex, lineIndex, { checked });
  };

  const handleDeltaCheckboxChange = (orderIndex, lineIndex) => {
    const line = ordersList[orderIndex].lines[lineIndex];
    if (line.deltaEnabled) {
      updateOrderLine(orderIndex, lineIndex, { deltaEnabled: false });
    } else {
      const deltaQuantity = line.quant - (line.newQuant !== undefined ? line.newQuant : line.quant);
      updateOrderLine(orderIndex, lineIndex, { deltaEnabled: true, deltaQuantity });
    }
  };

  const updateOrderLine = (orderIndex, lineIndex, updates) => {
    const newOrdersList = [...ordersList];
    newOrdersList[orderIndex].lines[lineIndex] = {
      ...newOrdersList[orderIndex].lines[lineIndex],
      ...updates,
    };
    setOrdersList(newOrdersList);
  };

  const updateOrderData = (orderIndex, lineIndex, newData) => {
    setOrdersList((prevOrders) => {
      const newOrders = [...prevOrders];
      newOrders[orderIndex].lines[lineIndex] = {
        ...newOrders[orderIndex].lines[lineIndex],
        ...newData,
      };
      return newOrders;
    });
  };

  const fetchSerialNumbers = async (partname) => {
    try {
      const response = await execute(
        "POST",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/approveLines/getSerialNumbers`,
        { partname }
      );

      if (response?.serials) {
        setSerialNumbers((prev) => ({
          ...prev,
          [partname]: response.serials,
        }));
      }
    } catch (error) {
      showError("Failed to load serial numbers");
    }
  };

  const handleConfirmCheckedLines = async (order) => {
    setLoading(true);
    
    const checkedLines = order.lines.filter((line) => line.checked);

    if (checkedLines.length === 0) {
      setLoading(false);
      showError("Please select lines to approve");
      return;
    }

    const { changedAmountLines, hasZeroQuantityLine, hasBatchOrNote } =
      analyzeCheckedLines(checkedLines, user.email);

    try {
      await sendApprovalRequest(order, checkedLines, {
        changedAmountLines,
        hasZeroQuantityLine,
        hasBatchOrNote,
      });
      
      showSuccess("Order lines approved successfully");
      await fetchOrders();
    } catch (error) {
      showError(error.message || "Failed to approve order lines");
      setOrdersList((prevOrders) => {
        const orderIndex = prevOrders.findIndex((o) => o._uniqueId === order._uniqueId);
        if (orderIndex === NOT_FOUND_INDEX) return prevOrders;
        
        const updatedOrders = [...prevOrders];
        updatedOrders[orderIndex] = {
          ...updatedOrders[orderIndex],
          lines: updatedOrders[orderIndex].lines.map((line) => ({
            ...line,
            checked: false,
            deltaEnabled: false,
          })),
        };
        return updatedOrders;
      });
    } finally {
      setLoading(false);
    }
  };

  const analyzeCheckedLines = (checkedLines, userEmail) => {
    let changedAmountLines = false;
    let hasZeroQuantityLine = false;
    let hasBatchOrNote = false;

    checkedLines.forEach((line) => {
      line.approvedBy = userEmail;
      
      if (line.newQuant !== undefined && line.newQuant !== line.quant) {
        changedAmountLines = true;
      }
      
      if (line.newQuant === 0) {
        hasZeroQuantityLine = true;
      }
      
      if (line.batch || line.note) {
        hasBatchOrNote = true;
      }
    });

    return { changedAmountLines, hasZeroQuantityLine, hasBatchOrNote };
  };

  const sendApprovalRequest = async (order, checkedLines, flags) => {
    const { changedAmountLines, hasZeroQuantityLine, hasBatchOrNote } = flags;

    if (order.itsTomorrow) {
      await handleSendEmail({ order, reason: "tomorrow" });
    }

    if (hasZeroQuantityLine) {
      checkedLines.forEach((line) => {
        if (line.newQuant === 0) {
          handleSendEmail({ order, line, reason: "zeroQuant" });
        }
      });
    }

    const endpoint = !changedAmountLines && !hasBatchOrNote
      ? "/api/approveLines/approve"
      : "/api/approveLines/approveWithAmoountChanges";

    const payload = !changedAmountLines && !hasBatchOrNote
      ? { order, checkedLines }
      : { order: { ...order, lines: checkedLines } };

    const response = await execute(
      "PATCH",
      `${process.env.REACT_APP_BACKEND_BASE_URL}${endpoint}`,
      payload
    );

    if (response.status !== 200 && response.status !== 400) {
      throw new Error(response.error || "Unknown error occurred");
    }
  };

  const handleToggleReviewed = async (order) => {
    const orderIndex = ordersList.findIndex((o) => o._uniqueId === order._uniqueId);
    if (orderIndex === NOT_FOUND_INDEX) return;

    const isCurrentlyReviewed = order.reviewed;
    const endpoint = isCurrentlyReviewed
      ? "/api/approveLines/unsetReviewed"
      : "/api/approveLines/setReviewed";

    try {
      await execute(
        "PATCH",
        `${process.env.REACT_APP_BACKEND_BASE_URL}${endpoint}`,
        { orderNumber: order.ordname }
      );

      const newOrdersList = [...ordersList];
      newOrdersList[orderIndex] = {
        ...newOrdersList[orderIndex],
        reviewed: !isCurrentlyReviewed,
      };
      setOrdersList(newOrdersList);
    } catch (error) {
      showError("Failed to update reviewed status");
    }
  };

  const handleSendEmail = async ({ order, line, reason }) => {
    try {
      await execute(
        "POST",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/approveLines/sendEmail`,
        { order: { ...order, reason }, line }
      );
    } catch (error) {
      showError("Failed to send email notification");
    }
  };

  const showError = (message) => {
    setSnackbar({ open: true, message, severity: "error" });
  };

  const showSuccess = (message) => {
    setSnackbar({ open: true, message, severity: "success" });
  };

  return {
    ordersList,
    loading,
    serialNumbers,
    snackbar,
    setSnackbar,
    handleQuantityChange,
    handleLineCheckboxChange,
    handleDeltaCheckboxChange,
    handleConfirmCheckedLines,
    handleToggleReviewed,
    fetchSerialNumbers,
    updateOrderData,
    fetchOrders,
  };
};

export default useApproveLines;